// src/hooks/useProductSchema.js
import { useState, useEffect, useMemo } from "react";
import { getProductByCode } from "../constants/products";
import { getFontsByProductCode } from "../constants/fonts";
import { getColorsByProductCode } from "../constants/colors";
import { getThicknessByProductCode } from "../constants/thicknesses";
import { getFinishByProductCode } from "../constants/finishes";
import { getHardwareByProductCode } from "../constants/hardwares";

export const useProductSchema = (
  mivaGlobals,
  selectedFont,
  setSelectedFont,
  setSelectedColor,
  setSelectedSize,
  setThickness,
  setFinish,
  setHardware,
  selectedColor,
  selectedSize,
  thickness,
  finish,
  hardware
) => {
  const [productSchema, setProductSchema] = useState(null);

  // Memoize size options for stability
  const sizeOptions = useMemo(() => {
    if (!mivaGlobals?.productcode) return [];
    const product = getProductByCode(mivaGlobals.productcode);
    if (!product) return [];

    return product.code === "ML1000" && selectedFont
      ? product.fonts?.find((f) => f.code === selectedFont)?.sizes || []
      : product.product_attributes?.size?.options || [];
  }, [mivaGlobals?.productcode, selectedFont]);

  useEffect(() => {
    if (!mivaGlobals?.productcode) {
      setProductSchema({});
      return;
    }

    const product = getProductByCode(mivaGlobals.productcode);
    if (!product) {
      setProductSchema({});
      return;
    }

    const fontOptions = getFontsByProductCode(mivaGlobals.productcode) || [];
    if (
      fontOptions.length > 0 &&
      !selectedFont &&
      typeof setSelectedFont === "function"
    ) {
      const defaultFont = fontOptions[0].code;
      setSelectedFont(defaultFont);
    }

    // Filter hardware options based on thickness
    const allHardwareOptions =
      getHardwareByProductCode(mivaGlobals.productcode) || [];
    const hardwareOptions = allHardwareOptions.length
      ? allHardwareOptions.filter((item) => {
          if (item.code === "Mounting_Glued_Stud") {
            return thickness === "FCO_18";
          }
          if (item.code === "Mounting_Flush_Stud") {
            return thickness !== "FCO_18";
          }
          return true;
        })
      : [{ code: "Mounting_Plain", name: "Plain" }];

    const schema = { productCode: mivaGlobals.productcode };
    if (product.product_attributes?.phrase) {
      schema.phrase = {
        ...product.product_attributes.phrase,
        options: undefined,
      };
    }
    if (product.product_attributes?.color) {
      schema.color = {
        ...product.product_attributes.color,
        options: getColorsByProductCode(mivaGlobals.productcode) || [],
      };
    }
    if (product.product_attributes?.font) {
      schema.font = {
        ...product.product_attributes.font,
        options: fontOptions,
      };
    }
    if (product.product_attributes?.size) {
      schema.size = {
        ...product.product_attributes.size,
        options: sizeOptions,
      };
    }
    if (product.product_attributes?.thickness) {
      schema.thickness = {
        ...product.product_attributes.thickness,
        options: getThicknessByProductCode(mivaGlobals.productcode) || [],
      };
    }
    if (product.product_attributes?.finish) {
      schema.finish = {
        ...product.product_attributes.finish,
        options: getFinishByProductCode(mivaGlobals.productcode) || [],
      };
    }
    if (product.product_attributes?.hardware) {
      schema.hardware = {
        ...product.product_attributes.hardware,
        options: hardwareOptions,
      };
    }
    setProductSchema(schema);

    // Set default values
    if (
      schema.color?.options?.length &&
      (!selectedColor ||
        !schema.color.options.some((opt) => opt.rgb === selectedColor)) &&
      typeof setSelectedColor === "function"
    ) {
      const defaultColor = schema.color.options[0].rgb;
      setSelectedColor(defaultColor);
    }
    if (
      schema.size?.options?.length &&
      (!selectedSize ||
        !schema.size.options.some((opt) => opt.code === selectedSize)) &&
      typeof setSelectedSize === "function"
    ) {
      const defaultSize = schema.size.options[0].code;
      setSelectedSize(defaultSize);
    }
    const thicknessOptions = getThicknessByProductCode(mivaGlobals.productcode);
    if (
      schema.thickness?.options?.length &&
      (!thickness || !thicknessOptions.some((opt) => opt.code === thickness)) &&
      typeof setThickness === "function"
    ) {
      const defaultThickness = schema.thickness.options[0]?.code || "";
      setThickness(defaultThickness);
    }
    const finishOptions = getFinishByProductCode(mivaGlobals.productcode);
    if (
      schema.finish?.options?.length &&
      (!finish || !finishOptions.some((f) => f.code === finish)) &&
      typeof setFinish === "function"
    ) {
      const defaultFinish =
        schema.finish.options.find((opt) => opt.code === "FCAL_NSA") ||
        schema.finish.options[0];
      setFinish(defaultFinish?.code || "");
    }
    if (
      schema.hardware?.options?.length &&
      (!hardware || !hardwareOptions.some((opt) => opt.code === hardware)) &&
      typeof setHardware === "function"
    ) {
      let defaultHardware = hardwareOptions[0];
      if (mivaGlobals.productcode === "BL40100") {
        const studMount = hardwareOptions.find(
          (opt) =>
            opt.code === "Mounting_Flush_Stud" ||
            opt.code === "Mounting_Glued_Stud"
        );
        defaultHardware = studMount || defaultHardware;
      }
      setHardware(defaultHardware?.code || "");
    }
  }, [
    mivaGlobals?.productcode,
    selectedFont,
    setSelectedFont,
    setSelectedColor,
    setSelectedSize,
    setThickness,
    setFinish,
    setHardware,
    sizeOptions,
    selectedColor,
    selectedSize,
    thickness,
    finish,
    hardware,
  ]);

  return productSchema;
};
